import { useEffect, useMemo, useState } from 'react'
import { Pause, Play, RotateCcw, Video, X } from 'lucide-react'
import clsx from 'clsx'
import { useSpatialStore } from '@/features/spatial-model/store.js'
import { validateRoute } from '@/features/validation/routing.js'

const SPEED_OPTIONS = [
  { value: 0.5, label: '0.5×' },
  { value: 1, label: '1×' },
  { value: 2, label: '2×' },
  { value: 4, label: '4×' },
]

export default function RouteControls() {
  const graph = useSpatialStore((s) => s.graph)
  const setRoutePreview = useSpatialStore((s) => s.setRoutePreview)
  const setBottomTab = useSpatialStore((s) => s.setBottomTab)
  const routePreview = useSpatialStore((s) => s.routePreview)
  const walkthrough = useSpatialStore((s) => s.walkthrough)
  const startWalkthrough = useSpatialStore((s) => s.startWalkthrough)
  const pauseWalkthrough = useSpatialStore((s) => s.pauseWalkthrough)
  const resetWalkthrough = useSpatialStore((s) => s.resetWalkthrough)
  const setWalkthroughSpeed = useSpatialStore((s) => s.setWalkthroughSpeed)
  const setWalkthroughFollow = useSpatialStore((s) => s.setWalkthroughFollow)
  const setWalkthroughProgress = useSpatialStore((s) => s.setWalkthroughProgress)

  const startOptions = useMemo(() => {
    const linkedAnchors = graph.anchors
      .filter((a) => !!a.linked_node_id)
      .map((a) => ({
        id: `anchor:${a.id}`,
        label: `Anchor · ${a.name || a.external_code || a.id.slice(0, 6)}`,
        nodeId: a.linked_node_id,
        kind: 'anchor',
        anchorType: a.anchor_type,
      }))
    const allNodes = graph.nodes.map((n) => ({
      id: `node:${n.id}`,
      label: `Node · ${n.name || n.id.slice(0, 6)}`,
      nodeId: n.id,
      kind: 'node',
      nodeType: n.node_type,
    }))
    return [...linkedAnchors, ...allNodes]
  }, [graph.anchors, graph.nodes])

  const destOptions = useMemo(() => {
    const linkedDestinations = graph.destinations
      .filter((d) => !!d.linked_node_id)
      .map((d) => ({
        id: `dest:${d.id}`,
        label: `Destination · ${d.name || d.id.slice(0, 6)}`,
        nodeId: d.linked_node_id,
        kind: 'destination',
      }))
    const allNodes = graph.nodes.map((n) => ({
      id: `node:${n.id}`,
      label: `Node · ${n.name || n.id.slice(0, 6)}`,
      nodeId: n.id,
      kind: 'node',
      nodeType: n.node_type,
    }))
    return [...linkedDestinations, ...allNodes]
  }, [graph.destinations, graph.nodes])

  const defaultStart = useMemo(() => {
    const entryAnchor = startOptions.find((o) => o.kind === 'anchor' && o.anchorType === 'entry')
    if (entryAnchor) return entryAnchor.id
    const firstAnchor = startOptions.find((o) => o.kind === 'anchor')
    if (firstAnchor) return firstAnchor.id
    const startNode = startOptions.find((o) => o.kind === 'node' && o.nodeType === 'start')
    if (startNode) return startNode.id
    return startOptions[0]?.id || ''
  }, [startOptions])

  const defaultDest = useMemo(() => {
    const firstDestination = destOptions.find((o) => o.kind === 'destination')
    if (firstDestination) return firstDestination.id
    const destinationNode = destOptions.find((o) => o.kind === 'node' && o.nodeType === 'destination')
    if (destinationNode) return destinationNode.id
    // Avoid colliding with the default start when both fall back to "first node".
    const firstDifferent = destOptions.find((o) => o.id !== defaultStart)
    return firstDifferent?.id || destOptions[0]?.id || ''
  }, [destOptions, defaultStart])

  const [start, setStart] = useState('')
  const [dest, setDest] = useState('')
  const [error, setError] = useState(null)

  // Apply sensible defaults when the user hasn't picked anything yet, and
  // clear stale selections that no longer exist in the option lists.
  useEffect(() => {
    if (!start && defaultStart) {
      setStart(defaultStart)
    } else if (start && !startOptions.some((o) => o.id === start)) {
      setStart(defaultStart || '')
    }
    if (!dest && defaultDest && defaultDest !== (start || defaultStart)) {
      setDest(defaultDest)
    } else if (dest && !destOptions.some((o) => o.id === dest)) {
      setDest(defaultDest || '')
    }
  }, [startOptions, destOptions, start, dest, defaultStart, defaultDest])

  const buildRoute = () => {
    setError(null)
    const startNode = startOptions.find((o) => o.id === start)?.nodeId
    const destNode = destOptions.find((o) => o.id === dest)?.nodeId
    const result = validateRoute(graph, startNode, destNode)
    if (!result.ok) {
      setError(result.message)
      setRoutePreview(null)
      return null
    }
    setRoutePreview(result.route)
    setBottomTab('route')
    return result.route
  }

  const onPreview = () => {
    const route = buildRoute()
    if (route) {
      // Auto-start the walkthrough when the user clicks Preview.
      requestAnimationFrame(() => startWalkthrough())
    }
  }

  const onPlayPause = () => {
    if (!routePreview) {
      onPreview()
      return
    }
    if (walkthrough.running) pauseWalkthrough()
    else startWalkthrough()
  }

  const onClear = () => {
    setRoutePreview(null)
    setStart('')
    setDest('')
    setError(null)
  }

  const hasPath = !!routePreview?.path?.length

  return (
    <div className="col gap-8" style={{ width: '100%' }}>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        <select
          value={start}
          onChange={(e) => setStart(e.target.value)}
          style={{ minWidth: 180 }}
          aria-label="Start point"
        >
          <option value="">Start…</option>
          {startOptions.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
        <select
          value={dest}
          onChange={(e) => setDest(e.target.value)}
          style={{ minWidth: 180 }}
          aria-label="Destination"
        >
          <option value="">Destination…</option>
          {destOptions.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
        {hasPath && (
          <button
            type="button"
            className="btn btn--sm"
            onClick={onClear}
            title="Clear route"
            aria-label="Clear route"
          >
            <X size={12} />
          </button>
        )}
      </div>

      {error && (
        <div className="auth-error" role="alert">
          <span>{error}</span>
        </div>
      )}

      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn--sm"
          onClick={onPlayPause}
          title={walkthrough.running ? 'Pause walkthrough' : 'Play walkthrough'}
        >
          {walkthrough.running ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Play</>}
        </button>
        <button
          type="button"
          className="btn btn--sm"
          onClick={resetWalkthrough}
          disabled={walkthrough.progress < 1e-3 && !walkthrough.running}
          title="Restart walkthrough"
        >
          <RotateCcw size={12} /> Restart
        </button>

        <div className="row" style={{ gap: 4, alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: 12 }}>Speed</span>
          {SPEED_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              className={clsx('btn btn--sm', walkthrough.speed === o.value && 'btn--primary')}
              onClick={() => setWalkthroughSpeed(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          className={clsx('btn btn--sm', walkthrough.follow && 'btn--primary')}
          onClick={() => setWalkthroughFollow(!walkthrough.follow)}
          title="Camera follows the walker"
        >
          <Video size={12} /> Follow
        </button>

        <input
          type="range"
          min={0}
          max={1}
          step={0.001}
          value={walkthrough.progress}
          onChange={(e) => {
            if (walkthrough.running) pauseWalkthrough()
            setWalkthroughProgress(parseFloat(e.target.value))
          }}
          style={{ flex: 1, minWidth: 160, accentColor: 'var(--accent)' }}
          aria-label="Walkthrough progress"
        />
        <span className="muted" style={{ fontSize: 12, fontFamily: 'var(--font-mono)', minWidth: 38, textAlign: 'right' }}>
          {Math.round(walkthrough.progress * 100)}%
        </span>
      </div>
    </div>
  )
}
